// Yoast-style SEO checks for the admin blog editor. Pure functions.
export function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

export function wordCount(html) {
  const t = stripHtml(html);
  if (!t) return 0;
  return t.split(/\s+/).length;
}

export function fleschReadingEase(html) {
  const text = stripHtml(html);
  if (!text) return 0;
  const sentences = text.split(/[.!?]+/).filter(Boolean).length || 1;
  const words = text.split(/\s+/).filter(Boolean);
  const w = words.length || 1;
  const syllables = words.reduce((n, word) => {
    const s = word.toLowerCase().replace(/[^a-z]/g, "").replace(/e$/,'').match(/[aeiouy]{1,2}/g);
    return n + Math.max(1, s ? s.length : 1);
  }, 0);
  return Math.max(0, Math.min(100, 206.835 - 1.015 * (w / sentences) - 84.6 * (syllables / w)));
}

export function readabilityGrade(score) {
  if (score >= 70) return { grade: "Easy", tone: "good" };
  if (score >= 60) return { grade: "OK", tone: "ok" };
  if (score >= 50) return { grade: "Fairly hard", tone: "warn" };
  return { grade: "Difficult", tone: "bad" };
}

function includes(hay, needle) {
  if (!hay || !needle) return false;
  return hay.toLowerCase().includes(needle.toLowerCase());
}

export function runSeoChecks({
  title = "",
  slug = "",
  metaTitle = "",
  metaDescription = "",
  focusKeyphrase = "",
  excerpt = "",
  bodyHtml = "",
  coverImageUrl = "",
}) {
  const checks = [];
  const push = (level, msg) => checks.push({ level, msg });

  // Basic presence
  push(title ? "good" : "bad", title ? "Post title is set." : "Add a post title.");
  push(slug ? "good" : "bad", slug ? `Slug: /blog/${slug}` : "Set a URL slug.");
  push(coverImageUrl ? "good" : "warn", coverImageUrl ? "Cover image is set." : "Add a cover image (used for OG previews).");

  // Meta title length 30-60
  const mtLen = (metaTitle || title || "").length;
  if (mtLen === 0) push("bad", "Meta title is empty.");
  else if (mtLen < 30) push("warn", `Meta title is short (${mtLen} chars). Aim for 50–60.`);
  else if (mtLen > 60) push("warn", `Meta title is long (${mtLen} chars). Google may cut it off.`);
  else push("good", `Meta title length is optimal (${mtLen} chars).`);

  // Meta description length 120-160
  const mdLen = (metaDescription || excerpt || "").length;
  if (mdLen === 0) push("bad", "Meta description is empty.");
  else if (mdLen < 120) push("warn", `Meta description is short (${mdLen} chars). Aim for 140–158.`);
  else if (mdLen > 160) push("warn", `Meta description is long (${mdLen} chars). Google may truncate.`);
  else push("good", `Meta description length is optimal (${mdLen} chars).`);

  // Focus keyphrase checks
  if (!focusKeyphrase) {
    push("warn", "Add a focus keyphrase to unlock keyword checks.");
  } else {
    const fk = focusKeyphrase.trim();
    push(includes(title, fk) ? "good" : "bad", `Focus keyphrase ${includes(title, fk) ? "appears" : "MISSING"} in the title.`);
    push(includes(slug, fk.replace(/\s+/g, "-")) ? "good" : "warn", `Focus keyphrase ${includes(slug, fk.replace(/\s+/g, "-")) ? "appears" : "MISSING"} in the slug.`);
    push(includes(metaTitle, fk) ? "good" : "warn", `Focus keyphrase ${includes(metaTitle, fk) ? "appears" : "MISSING"} in the meta title.`);
    push(includes(metaDescription, fk) ? "good" : "warn", `Focus keyphrase ${includes(metaDescription, fk) ? "appears" : "MISSING"} in the meta description.`);
    // First paragraph
    const firstP = (bodyHtml.match(/<p[^>]*>(.*?)<\/p>/i) || [])[1] || "";
    push(includes(stripHtml(firstP), fk) ? "good" : "warn", `Focus keyphrase ${includes(stripHtml(firstP), fk) ? "appears" : "MISSING"} in the first paragraph.`);
    // H2
    const h2Matches = Array.from(bodyHtml.matchAll(/<h2[^>]*>(.*?)<\/h2>/gi)).map(m => m[1]);
    const inH2 = h2Matches.some((h) => includes(stripHtml(h), fk));
    push(inH2 ? "good" : "warn", `Focus keyphrase ${inH2 ? "appears" : "MISSING"} in at least one H2.`);
    // Keyphrase density (0.5 - 2.5% is optimal)
    const text = stripHtml(bodyHtml);
    const wc = text ? text.split(/\s+/).length : 0;
    const occurrences = wc ? (text.toLowerCase().match(new RegExp(fk.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) || []).length : 0;
    const density = wc ? (occurrences / wc) * 100 : 0;
    if (wc < 100) push("warn", "Content too short to check keyphrase density.");
    else if (density < 0.3) push("warn", `Keyphrase density is low (${density.toFixed(2)}%). Aim for 0.5–2.5%.`);
    else if (density > 3.5) push("warn", `Keyphrase density is high (${density.toFixed(2)}%). Google may see this as spammy.`);
    else push("good", `Keyphrase density is healthy (${density.toFixed(2)}%).`);
  }

  // Word count
  const wc = wordCount(bodyHtml);
  if (wc === 0) push("bad", "Post body is empty.");
  else if (wc < 300) push("warn", `Post is short (${wc} words). Longer posts (900+) tend to rank better.`);
  else push("good", `Post length is healthy (${wc} words).`);

  // Images with alt text
  const imgs = Array.from((bodyHtml.matchAll(/<img[^>]*>/gi) || []));
  if (imgs.length) {
    const missingAlt = imgs.filter((m) => !/alt\s*=\s*"[^"]+"/i.test(m[0])).length;
    if (missingAlt) push("warn", `${missingAlt} image(s) missing alt text.`);
    else push("good", "All inline images have alt text.");
  }

  // Internal & external links
  const internal = (bodyHtml.match(/href="\/(blog|services|case-studies|pricing|contact|about)/gi) || []).length;
  const external = (bodyHtml.match(/href="https?:\/\//gi) || []).length;
  if (internal === 0) push("warn", "No internal links found. Link to relevant Zenith Ranking pages.");
  else push("good", `${internal} internal link(s) found.`);
  if (external === 0) push("warn", "No outbound links found. Cite at least one authoritative source.");
  else push("good", `${external} external link(s) found.`);

  // Readability
  const score = fleschReadingEase(bodyHtml);
  const g = readabilityGrade(score);
  const readabilityLevel = g.tone === "good" ? "good" : (g.tone === "ok" ? "good" : "warn");
  push(readabilityLevel, `Readability: ${g.grade} (Flesch ${score.toFixed(0)}).`);

  const stats = {
    good: checks.filter((c) => c.level === "good").length,
    warn: checks.filter((c) => c.level === "warn").length,
    bad: checks.filter((c) => c.level === "bad").length,
    score,
    wordCount: wc,
  };
  return { checks, stats };
}
